import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Plus,
  Trash2,
  HelpCircle,
  Database,
  Type,
  Route,
  Image,
  Link,
  List,
  ToggleLeft,
  Hash,
  FileText,
} from "lucide-react";
import clsx from "clsx";
import { CatalogContent } from "../../api";
import { LogoItem } from "@onecreator/shared";
import { WorkSummary } from "@onecreator/shared";
import type { BindingKind, FieldSchema } from "@onecreator/shared";
import { EntityRelation, EntityType } from "@onecreator/shared";

interface PropValue {
  kind: BindingKind;
  value?: any;
  path?: string;
}

interface PropsEditorProps {
  sectionId: string;
  props: Record<string, PropValue | any>;
  variant: string;
  onChange: (props: Record<string, PropValue | any>) => void;
  canEdit: boolean;
  bindingTypes: Record<string, any>;
  catalog: CatalogContent;
}

// Icone per tipo di campo
const FIELD_ICONS: Record<string, React.ElementType> = {
  string: Type,
  richtext: FileText,
  image: Image,
  url: Link,
  link: Link,
  boolean: ToggleLeft,
  number: Hash,
  enum: List,
  entitySelection: Database,
  faqItems: List,
  staffMembers: List,
  schedule: List,
  logoGrid: List,
  stringArray: List,
  statsArray: List,
  imageArray: Image,
  workSummaryArray: List,
  entityRelation: Database,
  entityRelationArray: List,
};

export function PropsEditor({
  sectionId,
  props,
  variant,
  onChange,
  canEdit,
  bindingTypes: _bindingTypes,
  catalog,
}: PropsEditorProps) {
  const section = catalog.sections[sectionId];

  const schema = {
    ...(section.props ?? {}),
    ...(section.variantProps?.[variant] ?? {}),
  };

  const schemaFields = Object.keys(schema);

  // Se non c'è schema, mostra editor JSON raw
  if (schemaFields.length === 0) {
    return (
      <RawPropsEditor props={props} onChange={onChange} canEdit={canEdit} />
    );
  }

  const updateProp = (fieldName: string, value: PropValue | any) => {
    onChange({ ...props, [fieldName]: value });
  };

  const removeProp = (fieldName: string) => {
    const newProps = { ...props };
    delete newProps[fieldName];
    onChange(newProps);
  };

  // Campi definiti nello schema
  const definedFields = schemaFields;
  // Campi extra (presenti in props ma non nello schema)
  const extraFields = Object.keys(props || {}).filter(
    (k) => !schemaFields.includes(k),
  );

  return (
    <div className="space-y-4">
      {/* Schema-defined fields */}
      {definedFields.map((fieldName) => {
        const fieldSchema = schema[fieldName];
        const currentValue = props?.[fieldName];

        return (
          <PropFieldEditor
            key={fieldName}
            fieldName={fieldSchema.label ?? fieldName}
            schema={fieldSchema}
            value={currentValue}
            onChange={(value) => updateProp(fieldName, value)}
            onRemove={() => removeProp(fieldName)}
            canEdit={canEdit}
          />
        );
      })}

      {/* Extra fields not in schema */}
      {extraFields.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <p className="text-xs text-gray-500 mb-2">Proprietà aggiuntive:</p>
          {extraFields.map((fieldName) => (
            <div
              key={fieldName}
              className="flex items-start space-x-2 mb-2 p-2 bg-gray-50 rounded"
            >
              <code className="text-xs text-gray-600 flex-shrink-0">
                {fieldName}
              </code>
              <pre className="text-xs text-gray-500 flex-1 overflow-auto">
                {JSON.stringify(props[fieldName], null, 2)}
              </pre>
              {canEdit && (
                <button
                  onClick={() => removeProp(fieldName)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Editor per singolo campo
function PropFieldEditor({
  fieldName,
  schema,
  value,
  onChange,
  onRemove: _onRemove,
  canEdit,
}: {
  fieldName: string;
  schema: FieldSchema;
  value: PropValue | any;
  onChange: (value: PropValue | any) => void;
  onRemove: () => void;
  canEdit: boolean;
}) {
  // Determina se il valore è già un binding object o un valore semplice
  const isBindingObject = value && typeof value === "object" && "kind" in value;
  const bindingKind: BindingKind = isBindingObject ? value.kind : "literal";
  const actualValue = isBindingObject
    ? bindingKind === "literal"
      ? value.value
      : value.path
    : value;

  const FieldIcon = FIELD_ICONS[schema.type] || Type;
  const isRequired = schema.required;
  const hasValue =
    actualValue !== undefined && actualValue !== null && actualValue !== "";

  const updateBinding = (kind: BindingKind, newValue: any) => {
    if (kind === "literal") {
      onChange({ kind: "literal", value: newValue });
    } else {
      onChange({ kind, path: newValue });
    }
  };

  const handleKindChange = (newKind: BindingKind) => {
    if (newKind === "literal") {
      onChange({ kind: "literal", value: schema.default || "" });
    } else {
      onChange({ kind: newKind, path: "" });
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      {/* Field header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <FieldIcon className="w-4 h-4 text-gray-400" />
          <label className="text-sm font-medium text-gray-700">
            {fieldName}
            {isRequired && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          {schema.description && (
            <div className="group relative">
              <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
              <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block z-10 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
                {schema.description}
              </div>
            </div>
          )}
        </div>

        {/* Binding type selector */}
        <div className="flex items-center space-x-1">
          <select
            value={bindingKind}
            onChange={(e) => handleKindChange(e.target.value as BindingKind)}
            disabled={!canEdit}
            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-gray-50"
          >
            <option value="literal">Valore statico</option>
            <option value="fromDto">Da DTO</option>
            <option value="fromRoute">Da URL</option>
          </select>
        </div>
      </div>

      {/* Field input based on type and binding kind */}
      <div className="mt-2">
        {bindingKind === "literal" ? (
          <LiteralValueEditor
            schema={schema}
            value={actualValue}
            onChange={(v) => updateBinding("literal", v)}
            canEdit={canEdit}
          />
        ) : (
          <PathEditor
            kind={bindingKind}
            value={actualValue || ""}
            onChange={(v) => updateBinding(bindingKind, v)}
            canEdit={canEdit}
          />
        )}
      </div>

      {/* Constraints info */}
      <div className="mt-1 flex items-center space-x-3 text-xs text-gray-400">
        {schema.maxLength && <span>Max {schema.maxLength} caratteri</span>}
        {schema.min !== undefined && schema.max !== undefined && (
          <span>
            Range: {schema.min}-{schema.max}
          </span>
        )}
        {schema.default !== undefined && !hasValue && (
          <span>Default: {String(schema.default)}</span>
        )}
      </div>
    </div>
  );
}

// Editor per valori literal
function LiteralValueEditor({
  schema,
  value,
  onChange,
  canEdit,
}: {
  schema: FieldSchema;
  value: any;
  onChange: (value: any) => void;
  canEdit: boolean;
}) {
  switch (schema.type) {
    case "string":
    case "image":
    case "url":
      return (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            schema.type === "image"
              ? "/assets/image.jpg"
              : schema.type === "url"
                ? "https://..."
                : ""
          }
          disabled={!canEdit}
          maxLength={schema.maxLength}
          className="input text-sm"
        />
      );

    case "richtext":
      return (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Inserisci il contenuto HTML o Markdown..."
          disabled={!canEdit}
          rows={4}
          className="input text-sm font-mono"
        />
      );

    case "number":
      return (
        <input
          type="number"
          value={value ?? schema.default ?? ""}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : undefined)
          }
          min={schema.min}
          max={schema.max}
          disabled={!canEdit}
          className="input text-sm w-32"
        />
      );
    case "boolean":
      return (
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value ?? schema.default ?? false}
            onChange={(e) => onChange(e.target.checked)}
            disabled={!canEdit}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">
            {value ? "Attivo" : "Disattivo"}
          </span>
        </label>
      );
    case "enum":
      return (
        <select
          value={value ?? schema.default ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={!canEdit}
          className="input text-sm"
        >
          <option value="">-- Seleziona --</option>
          {schema.values?.map((v) => (
            <option key={String(v)} value={String(v)}>
              {v}
            </option>
          ))}
        </select>
      );

    case "link":
      return <LinkEditor value={value} onChange={onChange} canEdit={canEdit} />;

    case "workSummaryArray":
      return (
        <WorkSummaryArrayEditor
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "imageArray":
    case "stringArray":
      return (
        <StringArrayEditor
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );

    case "staffMembers":
      return (
        <StaffMembersEditor
          schema={schema}
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "schedule":
    case "logoGrid":
      return (
        <LogoGridEditor value={value} onChange={onChange} canEdit={canEdit} />
      );
    case "entitySelection":
    case "faqItems":
    case "statsArray":
      return (
        <ComplexTypeEditor
          type={schema.type}
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "dateRange":
      return (
        <DateRangeEditor value={value} onChange={onChange} canEdit={canEdit} />
      );
    case "entityRelation":
      return (
        <EntityRelationEditor
          schema={schema}
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "entityRelationArray":
      return (
        <EntityRelationArrayEditor
          schema={schema}
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "workDetails":
      return (
        <WorkDetailsEditor
          schema={schema}
          value={value}
          onChange={onChange}
          canEdit={canEdit}
        />
      );
    case "iconArray":
      return (
        <IconArrayEditor value={value} onChange={onChange} canEdit={canEdit} />
      );

    default:
      return (
        <input
          type="text"
          value={
            typeof value === "object" ? JSON.stringify(value) : value || ""
          }
          onChange={(e) => onChange(e.target.value)}
          disabled={!canEdit}
          className="input text-sm"
        />
      );
  }
}

// Editor per path (fromDto, fromRoute)
function PathEditor({
  kind,
  value,
  onChange,
  canEdit,
}: {
  kind: "fromDto" | "fromRoute";
  value: string;
  onChange: (value: string) => void;
  canEdit: boolean;
}) {
  const placeholder =
    kind === "fromDto"
      ? "es. featuredItems, sectionBindings.heroTitle"
      : "es. params.id, query.lang";

  const Icon = kind === "fromDto" ? Database : Route;

  return (
    <div className="flex items-center space-x-2">
      <Icon className="w-4 h-4 text-blue-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={!canEdit}
        className="input text-sm font-mono flex-1"
      />
    </div>
  );
}

function StaffMembersEditor({
  schema,
  value,
  onChange,
  canEdit,
}: {
  schema: FieldSchema;
  value: any;
  onChange: (v: any) => void;
  canEdit: boolean;
}) {
  const items = Array.isArray(value) ? value : [];

  const fields = schema.items ?? {};

  const addItem = () => {
    const newItem = Object.fromEntries(
      Object.keys(fields).map((key) => [key, ""]),
    );

    onChange([...items, newItem]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_: any, i: number) => i !== index));
  };

  const updateItem = (index: number, field: string, fieldValue: any) => {
    const updated = [...items];

    updated[index] = {
      ...updated[index],
      [field]: fieldValue,
    };

    onChange(updated);
  };

  return (
    <div className="border-t border-gray-200 pt-4">
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Nessun membro configurato
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Membro {index + 1}</h4>

                {canEdit && (
                  <button
                    onClick={() => removeItem(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {Object.entries(fields).map(([fieldName, fieldSchema]) => (
                <div key={fieldName}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {fieldSchema.label ?? fieldName}
                  </label>

                  <input
                    type="text"
                    value={item[fieldName] || ""}
                    onChange={(e) =>
                      updateItem(index, fieldName, e.target.value)
                    }
                    disabled={!canEdit}
                    className="input w-full text-sm"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <button
          onClick={addItem}
          className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi membro
        </button>
      )}
    </div>
  );
}

function LogoGridEditor({
  value,
  onChange,
  canEdit,
}: {
  value: any;
  onChange: (v: any) => void;
  canEdit: boolean;
}) {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([
      ...items,
      {
        name: "",
        imageUrl: "",
        linkUrl: "",
      },
    ]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_: any, i: number) => i !== index));
  };

  const updateItem = (index: number, field: keyof LogoItem, value: string) => {
    const updated = [...items];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    onChange(updated);
  };

  return (
    <div className="border-t border-gray-200 pt-4">
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Nessun logo configurato</p>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">
                  Logo {index + 1}
                </h4>

                {canEdit && (
                  <button
                    onClick={() => removeItem(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <input
                type="text"
                value={item.name || ""}
                onChange={(e) => updateItem(index, "name", e.target.value)}
                placeholder="Nome partner"
                disabled={!canEdit}
                className="input w-full text-sm"
              />

              <input
                type="text"
                value={item.imageUrl || ""}
                onChange={(e) => updateItem(index, "imageUrl", e.target.value)}
                placeholder="/uploads/logo.png"
                disabled={!canEdit}
                className="input w-full text-sm"
              />

              <input
                type="text"
                value={item.linkUrl || ""}
                onChange={(e) => updateItem(index, "linkUrl", e.target.value)}
                placeholder="https://..."
                disabled={!canEdit}
                className="input w-full text-sm"
              />
            </div>
          ))}
        </div>
      )}
      <div className="mt-3">
        {canEdit && (
          <button
            onClick={addItem}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi logo
          </button>
        )}
      </div>
    </div>
  );
}

// Editor per link (label + href)
function LinkEditor({
  value,
  onChange,
  canEdit,
}: {
  value: any;
  onChange: (value: any) => void;
  canEdit: boolean;
}) {
  const link = value || { label: "", href: "" };

  return (
    <div className="grid grid-cols-2 gap-2">
      <input
        type="text"
        value={link.label || ""}
        onChange={(e) => onChange({ ...link, label: e.target.value })}
        placeholder="Label (es. Scopri di più)"
        disabled={!canEdit}
        className="input text-sm"
      />
      <input
        type="text"
        value={link.href || ""}
        onChange={(e) => onChange({ ...link, href: e.target.value })}
        placeholder="URL (es. /chi-siamo)"
        disabled={!canEdit}
        className="input text-sm font-mono"
      />
    </div>
  );
}

// Editor per array di stringhe
function StringArrayEditor({
  value,
  onChange,
  canEdit,
  placeholder,
}: {
  value: any;
  onChange: (value: any) => void;
  canEdit: boolean;
  placeholder?: string;
}) {
  const items: string[] = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([...items, ""]);
  };

  const updateItem = (index: number, newValue: string) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    const newItems = [...items];

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [
      newItems[targetIndex],
      newItems[index],
    ];

    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center space-x-2">
          {canEdit && (
            <div className="flex flex-col">
              <button
                onClick={() => moveItem(index, "up")}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <ChevronUp className="w-3 h-3" />
              </button>

              <button
                onClick={() => moveItem(index, "down")}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          )}

          <input
            type="text"
            value={item}
            placeholder={placeholder}
            onChange={(e) => updateItem(index, e.target.value)}
            disabled={!canEdit}
            className="input text-sm flex-1"
          />
          {canEdit && (
            <button
              onClick={() => removeItem(index)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <button
          onClick={addItem}
          className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
        >
          <Plus className="w-3 h-3 mr-1" />
          Aggiungi elemento
        </button>
      )}
    </div>
  );
}

function WorkSummaryArrayEditor({
  value,
  onChange,
  canEdit,
}: {
  value: WorkSummary[];
  onChange: (v: WorkSummary[]) => void;
  canEdit: boolean;
}) {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([
      ...items,
      {
        id: "",
        title: "",
        subtitle: "",
        imageUrl: "",
        author: "",
        dating: {
          start: "",
          end: "",
          display: "",
        },
      },
    ]);
  };

  const updateItem = (index: number, field: keyof WorkSummary, value: any) => {
    const updated = [...items];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    onChange(updated);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="border rounded-lg p-3 space-y-3">
          <div className="flex justify-between">
            <span className="font-medium">Elemento {index + 1}</span>

            {canEdit && (
              <button onClick={() => removeItem(index)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>

          <input
            className="input"
            placeholder="ID"
            value={item.id ?? ""}
            onChange={(e) => updateItem(index, "id", e.target.value)}
          />

          <input
            className="input"
            placeholder="Titolo"
            value={item.title ?? ""}
            onChange={(e) => updateItem(index, "title", e.target.value)}
          />

          <input
            className="input"
            placeholder="Sottotitolo"
            value={item.subtitle ?? ""}
            onChange={(e) => updateItem(index, "subtitle", e.target.value)}
          />

          <input
            className="input"
            placeholder="Immagine"
            value={item.imageUrl ?? ""}
            onChange={(e) => updateItem(index, "imageUrl", e.target.value)}
          />

          <input
            className="input"
            placeholder="Autore"
            value={item.author ?? ""}
            onChange={(e) => updateItem(index, "author", e.target.value)}
          />

          <input
            className="input"
            placeholder="Datazione"
            value={item.dating?.display ?? ""}
            onChange={(e) =>
              updateItem(index, "dating", {
                ...item.dating,
                display: e.target.value,
              })
            }
          />
        </div>
      ))}

      {canEdit && (
        <button
          onClick={addItem}
          className="flex items-center text-sm text-primary-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi elemento
        </button>
      )}
    </div>
  );
}

// Editor per tipi complessi (placeholder per future implementazioni)
function ComplexTypeEditor({
  type,
  value,
  onChange,
  canEdit,
}: {
  type: string;
  value: any;
  onChange: (value: any) => void;
  canEdit: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const typeLabels: Record<string, string> = {
    entitySelection: "Selezione entità (opere, documenti, ecc.)",
    faqItems: "Lista FAQ",
    staffMembers: "Membri staff",
    schedule: "Orari apertura",
    logoGrid: "Griglia loghi",
    statsArray: "Statistiche",
    entityRelation: "Relazione entità",
    entityRelationArray: "Lista relazioni entità",
  };

  return (
    <div className="border border-dashed border-gray-300 rounded p-3 bg-gray-50">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm text-gray-600">
          {typeLabels[type] || type}
        </span>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">
            Editor JSON (per tipi complessi):
          </p>
          <textarea
            value={JSON.stringify(value || {}, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value));
              } catch {}
            }}
            disabled={!canEdit}
            className="input text-xs font-mono w-full"
            rows={6}
          />
        </div>
      )}
    </div>
  );
}

function DateRangeEditor({
  value = {},
  onChange,
  canEdit,
}: {
  value: {
    start?: string;
    end?: string;
  };
  onChange: (value: any) => void;
  canEdit: boolean;
}) {
  return (
    <div className="space-y-2">
      <input
        className="input text-sm"
        placeholder="Anno inizio"
        value={value.start ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            start: e.target.value,
          })
        }
        disabled={!canEdit}
      />

      <input
        className="input text-sm"
        placeholder="Anno fine"
        value={value.end ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            end: e.target.value,
          })
        }
        disabled={!canEdit}
      />
    </div>
  );
}

function EntityRelationEditor({
  schema,
  value,
  onChange,
  canEdit,
}: {
  schema: FieldSchema;
  value?: EntityRelation;
  onChange: (v: EntityRelation) => void;
  canEdit: boolean;
}) {
  const relation: EntityRelation = {
    id: value?.id ?? "",
    label: value?.label ?? "",
    type: value?.type ?? schema.entityType ?? "work", //DA RIVEDERE
    role: value?.role ?? schema.role,
  };

  return (
    <div className="space-y-2">
      <input
        className="input text-sm"
        placeholder="Nome"
        value={relation.label ?? ""}
        onChange={(e) =>
          onChange({
            ...relation,
            label: e.target.value,
          })
        }
        disabled={!canEdit}
      />

      {!schema.entityType && (
        <select
          className="input text-sm"
          value={relation.type ?? "__select__"}
          onChange={(e) => {
            if (e.target.value === "__select__") return;
            onChange({
              ...relation,
              type: e.target.value as EntityType,
            });
          }}
          disabled={!canEdit}
        >
          <option value="__select__">Seleziona...</option>
          <option value="work">Opera</option>
          <option value="person">Persona</option>
          <option value="place">Luogo</option>
          <option value="event">Evento</option>
          <option value="building">Edificio</option>
          <option value="archival_fond">Fondo archivistico</option>
          <option value="heritage_record">Scheda</option>
        </select>
      )}

      {!schema.role && (
        <input
          className="input text-sm"
          placeholder="Ruolo"
          value={relation.role ?? ""}
          onChange={(e) =>
            onChange({
              ...relation,
              role: e.target.value,
            })
          }
          disabled={!canEdit}
        />
      )}
    </div>
  );
}

function EntityRelationArrayEditor({
  schema,
  value,
  onChange,
  canEdit,
}: {
  schema: FieldSchema;
  value: EntityRelation[];
  onChange: (v: EntityRelation[]) => void;
  canEdit: boolean;
}) {
  const items = Array.isArray(value) ? value : [];
  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    onChange([
      ...items,
      {
        id: "",
        label: "",
        type: schema.entityType ?? "work",
        role: schema.role ?? "",
      },
    ]);
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="border rounded-lg p-3 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm font-medium">
              {schema.label} {index + 1}
            </span>

            {canEdit && (
              <button onClick={() => removeItem(index)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>

          <EntityRelationEditor
            schema={schema}
            value={item}
            canEdit={canEdit}
            onChange={(newValue) => {
              const updated = [...items];
              updated[index] = newValue;
              onChange(updated);
            }}
          />
        </div>
      ))}

      {canEdit && (
        <button
          type="button"
          onClick={addItem}
          className="flex items-center text-sm text-primary-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi {schema.label?.toLowerCase()}
        </button>
      )}
    </div>
  );
}

function WorkDetailsEditor({
  schema,
  value = {},
  onChange,
  canEdit,
}: {
  schema: FieldSchema;
  value: any;
  onChange: (v: any) => void;
  canEdit: boolean;
}) {
  const fields = schema.fields ?? {};

  return (
    <div className="space-y-4">
      {Object.entries(fields).map(([key, fieldSchema]) => (
        <PropFieldEditor
          key={key}
          fieldName={fieldSchema.label ?? key}
          schema={fieldSchema}
          value={value[key]}
          canEdit={canEdit}
          onRemove={() => {}}
          onChange={(newValue) =>
            onChange({
              ...value,
              [key]: newValue,
            })
          }
        />
      ))}
    </div>
  );
}

export interface IconItem {
  url: string;
  label?: string;
}

function IconArrayEditor({
  value,
  onChange,
  canEdit,
}: {
  value: IconItem[];
  onChange: (v: IconItem[]) => void;
  canEdit: boolean;
}) {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([
      ...items,
      {
        url: "",
        label: "",
      },
    ]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (
    index: number,
    field: keyof IconItem,
    fieldValue: string,
  ) => {
    const updated = [...items];

    updated[index] = {
      ...updated[index],
      [field]: fieldValue,
    };

    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="border rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">Icona {index + 1}</span>

            {canEdit && (
              <button onClick={() => removeItem(index)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>

          <input
            className="input w-full text-sm"
            placeholder="/uploads/icona.svg"
            value={item.url}
            onChange={(e) => updateItem(index, "url", e.target.value)}
            disabled={!canEdit}
          />

          <input
            className="input w-full text-sm"
            placeholder="Etichetta (facoltativa)"
            value={item.label ?? ""}
            onChange={(e) => updateItem(index, "label", e.target.value)}
            disabled={!canEdit}
          />
        </div>
      ))}

      {canEdit && (
        <button
          onClick={addItem}
          className="flex items-center text-sm text-primary-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi icona
        </button>
      )}
    </div>
  );
}

// Fallback editor JSON raw
function RawPropsEditor({
  props,
  onChange,
  canEdit,
}: {
  props: Record<string, any>;
  onChange: (props: Record<string, any>) => void;
  canEdit: boolean;
}) {
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleChange = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch (e) {
      setJsonError("JSON non valido");
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        Nessuno schema disponibile per questa sezione. Modifica il JSON
        direttamente:
      </p>
      <textarea
        value={JSON.stringify(props || {}, null, 2)}
        onChange={(e) => handleChange(e.target.value)}
        disabled={!canEdit}
        className={clsx(
          "input text-xs font-mono w-full",
          jsonError && "border-red-300",
        )}
        rows={6}
      />
      {jsonError && <p className="text-xs text-red-500 mt-1">{jsonError}</p>}
    </div>
  );
}
